import React, { useState } from 'react';
import { useProjects } from '../contexts/ProjectContext';
import Icon from './AppIcon';

const SaveToProjectModal = () => {
  const {
    projects,
    saveModalState,
    closeSaveModal,
    saveImageToProject,
    createProject,
  } = useProjects();

  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [showExistingProjects, setShowExistingProjects] = useState(false);

  if (!saveModalState) return null;

  const { imageUrl, toolPath, toolName } = saveModalState;

  const handleSaveToProject = (projectId) => {
    saveImageToProject(projectId, imageUrl, toolPath);
    closeSaveModal();
    resetState();
  };

  const handleCreateAndSave = () => {
    if (!newProjectName.trim()) return;
    const newProject = createProject(newProjectName.trim());
    saveImageToProject(newProject.id, imageUrl, toolPath);
    closeSaveModal();
    resetState();
  };

  const handleDiscard = () => {
    closeSaveModal();
    resetState();
  };

  const resetState = () => {
    setIsCreatingNew(false);
    setNewProjectName('');
    setShowExistingProjects(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999] w-[340px]">
      <div className="glassmorphic-card rounded-[20px] border border-[rgba(254,131,56,0.3)] p-5 shadow-2xl">
        {/* Close */}
        <button
          onClick={handleDiscard}
          className="absolute top-3 right-3 text-white/50 hover:text-white transition-colors"
        >
          <Icon name="X" size={18} />
        </button>

        {/* Header */}
        <h2 className="text-white text-base font-bold mb-0.5">Save generated image</h2>
        <p className="text-white/40 text-xs mb-4">
          Generated with <span className="text-orange-400">{toolName}</span>
        </p>

        {/* Create New Project */}
        {isCreatingNew ? (
          <div className="mb-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateAndSave();
                }}
                placeholder="e.g. Green Living Room"
                className="flex-1 rounded-xl px-3 py-2.5 text-white placeholder:text-white/40 text-sm bg-white/5 border border-[rgba(254,131,56,0.15)] focus:border-[rgba(254,131,56,0.3)] focus:outline-none transition-colors"
                autoFocus
              />
              <button
                onClick={handleCreateAndSave}
                disabled={!newProjectName.trim()}
                className="px-3 py-2.5 glassmorphic-button rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsCreatingNew(true)}
            className="w-full glassmorphic-button rounded-xl py-2.5 text-white text-sm font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform mb-3"
          >
            <Icon name="Plus" size={14} />
            Create new project & save
          </button>
        )}

        {/* Save to Existing Project */}
        {showExistingProjects ? (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white/70 text-xs font-medium">Save to existing project:</h3>
              <button
                onClick={() => setShowExistingProjects(false)}
                className="text-white/30 hover:text-white/60 transition-colors"
              >
                <Icon name="ChevronUp" size={14} />
              </button>
            </div>
            {projects.length > 0 ? (
              <div className="space-y-1.5 max-h-[160px] overflow-y-auto custom-scrollbar">
                {projects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleSaveToProject(project.id)}
                    className="w-full text-left px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-orange-500/40 transition-all text-white text-sm font-medium flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Icon name="Folder" size={14} className="text-orange-500" />
                      <span>{project.name}</span>
                    </div>
                    <span className="text-white/30 text-xs">{project.images.length}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-white/30 text-xs text-center py-3">No projects yet — create one above</p>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowExistingProjects(true)}
            className="w-full rounded-xl py-2.5 text-white/70 text-sm font-medium flex items-center justify-center gap-2 hover:text-white transition-colors bg-white/5 border border-white/10 hover:border-white/20 mb-3"
          >
            <Icon name="FolderOpen" size={14} />
            Save to existing project
          </button>
        )}

        {/* Discard */}
        <button
          onClick={handleDiscard}
          className="w-full py-2 rounded-xl text-white/40 hover:text-red-400 text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
        >
          <Icon name="Trash2" size={12} />
          Discard image
        </button>
      </div>
    </div>
  );
};

export default SaveToProjectModal;
